import { Text } from 'foundations-components/components/Text';
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { Link } from 'quartz-routing/react-router';
import { Paths } from '../../constants/paths';
import { useGates } from '../../hooks/useGates';
import { useProductTypes } from '../../hooks/useProductTypeSearchParams';
import { useCrossSearchCount } from '../hooks/useCrossSearchCount';
import { useSolutionsPartnerCrossSearchCount } from '../hooks/useSolutionsPartnerCrossSearchCount';
import { useSearchResultsQuery } from '../hooks/useSearchResultsQuery';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getProductTypeLabel(productTypes) {
  if (productTypes.length === 1) {
    switch (productTypes[0]) {
      case 'APP':
        return unescapedText('marketplaceStorefront.searchResults.header.productTypeLabel.apps');
      case 'AGENT':
        return unescapedText('marketplaceStorefront.searchResults.header.productTypeLabel.agents');
      case 'WORKFLOW_TEMPLATE':
        return unescapedText('marketplaceStorefront.searchResults.header.productTypeLabel.workflowTemplates');
      case 'SOLUTIONS_PARTNER_PROFILE':
        return unescapedText('marketplaceStorefront.searchResults.header.productTypeLabel.solutionsPartners');
      default:
        return undefined;
    }
  }
  if (productTypes.includes('SOLUTIONS_PARTNER_PROFILE')) {
    return undefined;
  }
  if (productTypes.includes('WORKFLOW_TEMPLATE')) {
    return unescapedText('marketplaceStorefront.searchResults.header.productTypeLabel.appsAgentsAndWorkflowTemplates');
  }
  return unescapedText('marketplaceStorefront.searchResults.header.productTypeLabel.appsAndAgents');
}
function shouldShowCrossSearch(productTypes) {
  if (productTypes.includes('SOLUTIONS_PARTNER_PROFILE')) {
    return false;
  }
  return productTypes.length > 0;
}
export function SearchResultsHeader() {
  var _data$result$total;
  const searchParams = useSearchParams();
  const {
    data
  } = useSearchResultsQuery();
  const gates = useGates();
  const crossSearch = useCrossSearchCount();
  const solutionsPartnerCrossSearch = useSolutionsPartnerCrossSearchCount();
  const productTypes = useProductTypes();
  const search = searchParams.get('search');
  const count = (_data$result$total = data === null || data === void 0 ? void 0 : data.result.total) !== null && _data$result$total !== void 0 ? _data$result$total : 0;
  if (gates.workflowTemplatesPhase2) {
    var _solutionsPartnerCros;
    const productTypeLabel = getProductTypeLabel(productTypes);
    const showCrossSearch = shouldShowCrossSearch(productTypes) && solutionsPartnerCrossSearch.shouldShow;
    if (!productTypeLabel && !showCrossSearch) {
      return undefined;
    }
    return /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "200",
      paddingBlockEnd: "200",
      children: [productTypeLabel && /*#__PURE__*/_jsx(Text, {
        as: "h1",
        typography: "body-200",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "marketplaceStorefront.searchResults.header.resultsForProductType_jsx",
          elements: {
            Strong: props => /*#__PURE__*/_jsx(Text, Object.assign({
              as: "span",
              typography: "heading-100"
            }, props))
          },
          options: {
            count,
            productType: productTypeLabel
          }
        })
      }), showCrossSearch && productTypeLabel && search && /*#__PURE__*/_jsx(Text, {
        typography: "detail-100",
        color: "text-core-subtle",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "marketplaceStorefront.searchResults.header.crossSearchToSolutionsForProductType_jsx",
          elements: {
            Strong: props => /*#__PURE__*/_jsx(Text, Object.assign({
              as: "span",
              typography: "detail-100",
              fontWeight: "bold"
            }, props)),
            Link: props => /*#__PURE__*/_jsx(Link, Object.assign({
              to: search ? `${Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS}?eco_search=${encodeURIComponent(search)}` : Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS
            }, props))
          },
          options: {
            otherCount: I18n.formatNumber((_solutionsPartnerCros = solutionsPartnerCrossSearch.count) !== null && _solutionsPartnerCros !== void 0 ? _solutionsPartnerCros : 0),
            productType: productTypeLabel
          }
        })
      })]
    });
  }

  // Legacy behavior (phase 2 gate OFF)
  if (!search) return undefined;
  const showCrossLink = !gates.tabularSearch && (crossSearch.isProducts || crossSearch.isSolutions);
  if (showCrossLink) {
    var _crossSearch$count;
    const otherPath = crossSearch.isProducts ? Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS : Paths.SEARCH_RESULTS_PRODUCTS;
    return /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "200",
      paddingBlockEnd: "200",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h1",
        typography: "body-200",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: crossSearch.isProducts ? 'marketplaceStorefront.searchResults.header.resultsForQueryInApps_jsx' : 'marketplaceStorefront.searchResults.header.resultsForQueryInSolutions_jsx',
          elements: {
            Strong: props => /*#__PURE__*/_jsx(Text, Object.assign({
              as: "span",
              typography: "heading-100"
            }, props))
          },
          options: {
            count,
            query: search
          }
        })
      }), /*#__PURE__*/_jsx(Text, {
        typography: "detail-100",
        color: "text-core-subtle",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: crossSearch.isProducts ? 'marketplaceStorefront.searchResults.header.crossSearchLinkToSolutions_jsx' : 'marketplaceStorefront.searchResults.header.crossSearchLinkToApps_jsx',
          elements: {
            Strong: props => /*#__PURE__*/_jsx(Text, Object.assign({
              as: "span",
              typography: "detail-100",
              fontWeight: "bold"
            }, props)),
            Link: props => /*#__PURE__*/_jsx(Link, Object.assign({
              to: `${otherPath}?eco_search=${encodeURIComponent(search)}`
            }, props))
          },
          options: {
            otherCount: (_crossSearch$count = crossSearch.count) !== null && _crossSearch$count !== void 0 ? _crossSearch$count : 0
          }
        })
      })]
    });
  }
  return /*#__PURE__*/_jsx(Text, {
    as: "h1",
    typography: "body-200",
    paddingBlock: "200",
    children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "marketplaceStorefront.searchResults.header.resultsForQuery_jsx",
      elements: {
        Strong: props => /*#__PURE__*/_jsx(Text, Object.assign({
          as: "span",
          typography: "heading-100"
        }, props))
      },
      options: {
        count,
        query: search
      }
    })
  });
}